import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';

// Define user types as constants for consistent usage across the application
export const USER_TYPES = {
  UNREGISTERED: 'unregistered',
  BUYER: 'buyer',
  BRAND: 'brand'
};

const UserTypeContext = createContext();

export const useUserType = () => useContext(UserTypeContext);

export const UserTypeProvider = ({ children }) => {
  const { user } = useUser();
  const [userType, setUserType] = useState(USER_TYPES.UNREGISTERED);
  const [isUserTypeReady, setIsUserTypeReady] = useState(false);
  
  useEffect(() => {
    // Determine user type based on user object and authentication status
    const detectUserType = () => {
      // If user is not logged in, they are unregistered
      if (!user || !localStorage.getItem('access_token')) {
        setUserType(USER_TYPES.UNREGISTERED);
        setIsUserTypeReady(true);
        return;
      }
      
      // Check company type to determine user type
      if (user.company && user.company.type_of_business) {
        // If company type is 'brand', they are a brand/seller
        // Otherwise, they are a buyer
        const type = user.company.type_of_business === 'brand' 
          ? USER_TYPES.BRAND 
          : USER_TYPES.BUYER;
        
        setUserType(type);
      } else {
        // Default to BUYER if type can't be determined but user is logged in
        // This is a fallback case and should be investigated if happening frequently
        console.warn('User is authenticated but company type could not be determined. Defaulting to BUYER.');
        setUserType(USER_TYPES.BUYER);
      }
      
      setIsUserTypeReady(true);
    };
    
    detectUserType();
  }, [user]);
  
  return (
    <UserTypeContext.Provider value={{ 
      userType, 
      isUserTypeReady,
      isBrand: userType === USER_TYPES.BRAND,
      isBuyer: userType === USER_TYPES.BUYER,
      isUnregistered: userType === USER_TYPES.UNREGISTERED
    }}>
      {children}
    </UserTypeContext.Provider>
  );
};

export default UserTypeContext;
